import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { Check, Pencil } from 'lucide-react'
import { Card, Badge, Button } from '@/components/ui'
import { useAccountSettings } from '@/hooks/useAccountSettings'
import { useDashboard } from '@/hooks/useDashboard'
import { buildStepParams } from '@/lib/onboardingResume'

export interface DashboardChecklistProps {
  className?: string
}

const STEPS = [1, 2, 3, 4, 5, 6, 7, 8] as const

export function DashboardChecklist({ className }: DashboardChecklistProps) {
  const { t } = useTranslation('common')
  const { data: accountSettings } = useAccountSettings()
  // BUG-010: completion must reflect ACTUAL data, not just the onboarding_step
  // counter — advancing past an empty step used to false-✓ it (e.g. "Rooms
  // added ✓" with 0 units), and data created outside the wizard (e.g. an
  // invoice) never marked its step. Account-wide stats (null = all properties)
  // back the per-step truth where a count exists.
  const { data: stats } = useDashboard(null)

  const isCompleted = accountSettings?.settings?.onboarding_completed === true
  const onboardingStep = accountSettings?.settings?.onboarding_step ?? 0

  // Per-step data signal, or null when no stat exists for that step (steps 2
  // "Pricing set", 4 "Customer added", 6 "Readings recorded" aren't counted in
  // the dashboard stats yet — those fall back to the step counter). A fuller fix
  // would add a dedicated onboarding-status endpoint covering all 8 entities.
  const stepHasData = (step: number): boolean | null => {
    if (!stats) return null
    switch (step) {
      case 1: return stats.total_properties > 0            // Property created
      case 3: return stats.total_units > 0                 // Rooms added
      case 5: return stats.active_leases > 0               // Lease created
      case 7: return stats.pending_invoices > 0 || stats.recent_invoices.length > 0 // Invoice generated
      case 8: return stats.recent_payments.length > 0      // Payment recorded
      default: return null
    }
  }
  const isStepDone = (step: number): boolean => {
    if (isCompleted) return true
    const data = stepHasData(step)
    return data ?? step <= onboardingStep
  }

  // No account settings loaded yet → render nothing (avoid a flash).
  if (!accountSettings) return null

  // Show the checklist for: completed accounts (8/8 recap), in-progress accounts
  // (onboarding_step set), AND brand-new/fresh accounts with zero properties
  // (0/8). EP-109: this carousel is the single onboarding UI — it replaced the
  // old 3-step SetupGuide welcome that fresh accounts used to see. Established
  // pre-EP-43 accounts (data present, no onboarding flags) still see no widget.
  const isFreshAccount = stats != null && stats.total_properties === 0
  const hasOnboardingState =
    isCompleted || accountSettings.settings?.onboarding_step != null || isFreshAccount
  if (!hasOnboardingState) return null

  const completedCount = isCompleted ? STEPS.length : STEPS.filter(isStepDone).length
  // EP-155 decision D2 (owner, 2026-10-07): gone once all eight steps are
  // done. It used to stay at the top of the owner's page for good as an 8/8
  // recap.
  if (completedCount === STEPS.length) return null

  // EP-109 (owner call): sequential flow — only the CURRENT step (the first
  // not-done step) is active/green and actionable; every other step (already
  // done, or not yet reached) is disabled. Undefined once everything is done.
  const currentStep = STEPS.find((step) => !isStepDone(step))

  // EP-109 (owner call): the widget is NOT collapsible — the setup steps stay
  // fully visible at all times. No chevron, no toggle/localStorage state.
  return (
    <Card className={className}>
      <div className="flex items-center gap-2">
        <h3 className="font-semibold text-base flex-1">
          {t('dashboard.onboarding.title')}
        </h3>
        {/* Soft-green progress pill (mockup #42) — not the solid count-primary color. */}
        <Badge
          variant="default"
          className="rounded-full border-0 bg-success/15 text-success font-medium tabular-nums px-2.5"
        >
          {t('dashboard.onboarding.progress', { done: completedCount, total: STEPS.length })}
        </Badge>
      </div>
      <div
        id="dashboard-checklist-list"
        className="flex gap-3 sm:gap-4 overflow-x-auto mt-3 pb-1"
      >
        {STEPS.map((step) => {
          const stepDone = isStepDone(step)
          const isCurrent = step === currentStep
          const title = t(`dashboard.onboarding.step_${step}_title` as const)
          const description = t(`dashboard.onboarding.step_${step}_description` as const)

          return (
            <Card
              key={step}
              data-testid={`onboarding-step-${step}`}
              className={`flex-shrink-0 w-52 sm:w-56 min-h-[192px] ${isCurrent ? '' : 'opacity-60'}`}
              bodyClassName="flex flex-col h-full items-center text-center"
              compact
            >
              <div
                className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
                  isCurrent ? 'bg-primary/10 text-primary' : 'bg-base-200 text-base-content/40'
                }`}
              >
                {stepDone ? (
                  <Check className="w-5 h-5" aria-hidden="true" />
                ) : (
                  <Pencil className="w-5 h-5" aria-hidden="true" />
                )}
              </div>
              <h4 className="font-semibold text-sm mt-3">{title}</h4>
              <p className="text-xs text-base-content/60 mt-1 line-clamp-1 flex-1">
                {description}
              </p>
              {isCurrent ? (
                <Button
                  as={Link}
                  to={`/onboarding?${buildStepParams(step, accountSettings).toString()}`}
                  variant="primary"
                  size="sm"
                  className="mt-3 w-full"
                >
                  {t('dashboard.onboarding.cta_add')}
                </Button>
              ) : (
                <Button variant="ghost" size="sm" disabled className="mt-3 w-full">
                  {stepDone ? t('dashboard.onboarding.cta_done') : t('dashboard.onboarding.cta_add')}
                </Button>
              )}
            </Card>
          )
        })}
      </div>
    </Card>
  )
}
